import React from "react";
import { useStore } from "../../store/store";
import fractionToInt from "../../util/fractionToInt";
import { screenType } from "./defaults";

function centorSectionViewURLs(
  material: string,
  doorType: string,
  sill: string,
  cornerType: string,
  isPico: boolean,
) {
  const parentFolder = "CENTOR_SECTION_VIEW";

  if (doorType?.includes("Bi-fold")) {
    const isCorner = doorType.includes("Corner");
    const folder = isCorner ? "bifold_corners" : "bifolds";
    const materialLbl = material.includes("Clad") ? "CLAD" : "WOOD";
    const sillLbl =
      sill === "ADA Ultra Guide"
        ? "ADA_UG"
        : sill === "Ultra Guide"
          ? "UG"
          : sill === "Rounded Dam"
            ? "ROUND"
            : "SQUARE";

    return {
      headSill: `${parentFolder}/bifolds/${materialLbl}_HEAD_SILL_${sillLbl}`,
      jambLeg: isCorner
        ? `${parentFolder}/${folder}/${cornerType?.toUpperCase()}_CORNER_${materialLbl}_LEG_JAMB`
        : isPico
          ? `${parentFolder}/${folder}/${materialLbl}_LEG_JAMB_PICO`
          : `${parentFolder}/${folder}/${materialLbl}_LEG_JAMB`,
    };
  }

  if (doorType === "Swing") {
    const folder = "swings";
    const materialLbl = material.includes("Clad") ? "CLAD" : "WOOD";

    return {
      headSill: `${parentFolder}/${folder}/${materialLbl}_SILL`,
      jambLeg: `${parentFolder}/${folder}/${materialLbl}`,
    };
  }
}

function useScreenTypeSelector() {
  const addToQuote = useStore((state) => state.addToQuote);
  const doorType = useStore((state) => state.quote.doorType);
  const isCornerUnit = doorType?.includes("Corner");
  const isComboUnit = doorType?.includes("Combo");
  const material = useStore((state) => state.quote.material);
  const sillStyle = useStore((state) => state.quote.sillStyle);
  const direction = useStore((state) => state.quote.swingDirection);
  const cornerType = useStore((state) => state.quote.cornerType);
  const height = useStore((state) => state.quote.height);
  const heightFraction = useStore((state) => state.quote.heightFraction);
  const heightInt = fractionToInt(height, heightFraction);
  const unitSize = useStore((state) => state.quote.unitSize);
  const unitSizeFraction = useStore((state) => state.quote.unitSizeFraction);
  const rightWidth = useStore((state) => state.quote.cornerWidthRight);
  const rightFraction = useStore(
    (state) => state.quote.cornerWidthFractionRight,
  );
  const comboWindowWidth = useStore((state) => state.quote.comboWindowUnitSize);
  const comboWindowWidthFraction = useStore(
    (state) => state.quote.comboWindowUnitSizeFraction,
  );
  const width = fractionToInt(unitSize, unitSizeFraction);
  const widthRight = fractionToInt(rightWidth || "", rightFraction);
  const widthComboWindow = fractionToInt(
    comboWindowWidth || "",
    comboWindowWidthFraction,
  );
  const stileCategory = useStore((state) => state.quote.stileCategory);
  const screenTypeChoice = useStore((state) => state.quote.screenType);
  const addScreen = useStore((state) => state.quote.addScreen);
  const screenMax = cornerType === "Outside" ? 199 : 185;
  const screenMin = cornerType === "Outside" ? 193 : 179;
  const isPico = stileCategory === "Pico";
  const isPicoSwing = isPico && doorType === "Swing";

  //single screen cant have pocket in this range because it would add to the screen size exceeding 16ft
  const noSinglePocktRange = width >= 186.0625 && width <= 192 ? true : false;

  //single screen for pico swings cant have pocket if greater than 187 1/8"
  const noSinglePocktRangeForPicoSwing = isPicoSwing && width > 187.125;

  const cornerUnitOver16ft =
    isCornerUnit && (width > screenMax || widthRight > screenMax);

  const cornerUnitNoSinglePocketRange =
    isCornerUnit &&
    ((width > screenMin && width <= screenMax) ||
      (widthRight > screenMin && widthRight <= screenMax));

  const comboUnitOver16ft =
    isComboUnit && (width > 192 || widthComboWindow > 192);

  const comboUnitNoSinglePocketRange =
    isComboUnit &&
    ((width > 186.0625 && width <= 192) ||
      (widthComboWindow > 186.0625 && widthComboWindow <= 192));

  const isPocketAllowed =
    cornerUnitOver16ft || comboUnitOver16ft
      ? true
      : cornerUnitNoSinglePocketRange || comboUnitNoSinglePocketRange
        ? false
        : (!isCornerUnit &&
              !isComboUnit &&
              !isPicoSwing &&
              noSinglePocktRange &&
              !addScreen?.includes("Double")) ||
            (noSinglePocktRangeForPicoSwing && !addScreen?.includes("Double"))
          ? false
          : true;

  const widerThan16 =
    cornerUnitOver16ft || comboUnitNoSinglePocketRange
      ? true
      : cornerUnitNoSinglePocketRange || comboUnitNoSinglePocketRange
        ? false
        : !isCornerUnit && !isComboUnit && width > 192
          ? true
          : false;

  const widerThanLimitForPicoSwingSingleScreen =
    isPico && doorType === "Swing" && width > 193.875;

  const centorSectionImgs = centorSectionViewURLs(
    material,
    doorType,
    sillStyle || "",
    cornerType || "",
    stileCategory === "Pico",
  );

  const handleClick = (label: string) => {
    const checked = label === screenTypeChoice;

    let update = {
      screenType: "",
      addScreen: "",
      screenLayout: "",
      screenPocket: "",
      screenColor: "",
      jamb: "",
    };

    if (checked) {
      addToQuote(update);
    } else {
      update["screenType"] = label;
      addToQuote(update);
    }
  };

  const isCentor = screenTypeChoice?.includes("Centor");

  const isBrio = screenTypeChoice?.includes("Brio");

  const isAlumBifold =
    material === "All Aluminum" &&
    (doorType === "Bi-fold" || doorType === "Bi-fold Window");

  const hideScreens =
    (doorType === "Swing" && direction === "Inswing") || heightInt < 32
      ? true
      : false;

  const bifoldAlumNoScreen = isAlumBifold && sillStyle !== "Raised Sill";

  const centorCushion = 0.125; //added on 05/22/2025 per Brian because screen sizes were "too exact"

  const centorWidth = isAlumBifold ? width : width - 1 - centorCushion;

  const screenTypeChoices =
    centorWidth > 354 ||
    heightInt < 32.125 ||
    (isCornerUnit && cornerType === "Inside") ||
    isComboUnit
      ? screenType.filter((itm) => itm.id === 1)
      : isAlumBifold
        ? screenType.filter((itm) => itm.id === 2)
        : screenType;

  const brioOnlyOption =
    screenTypeChoices.length === 1 &&
    screenTypeChoices[0].label?.includes("Brio")
      ? true
      : false;

  const centorOnlyOption =
    screenTypeChoices.length === 1 &&
    screenTypeChoices[0].label?.includes("Centor")
      ? true
      : false;

  return {
    brioOnlyOption,
    hideScreens,
    isBrio,
    isCentor,
    handleClick,
    centorSectionImgs,
    widerThan16,
    widerThanLimitForPicoSwingSingleScreen,
    isPocketAllowed,
    screenTypeChoice,
    screenTypeChoices,
    noSinglePocktRange,
    width,
    widthRight,
    centorOnlyOption,
    bifoldAlumNoScreen,
  };
}

export default useScreenTypeSelector;
